import {useParams} from 'react-router-dom';
import { ProjectSupportComponents, NewProjectUpperBar } from "../000_ProjectTab"
import {FileManagerModule} from './FileManager_Module'
import {FileManagerToolBar} from './FileManager_ToolBar'
import {createFolderStructure, sanitizedSourceFilesPath, sanitizedTargetFilesPath} from './FileManager_support'

import { VideoComponent } from '../../../ApiCenter/VideoComponent';
import { useEffect, useRef, useState} from 'react';
import {AMModal, LoadingBar, ProgressionBar, dBConn} from '../../../ApiCenter/API_GET';
import { FileManagerModals } from './FileManager_Modals';


const FileManagerApp = (props) => {

    const urlParams = useParams();
    // Use projectId from props if provided (embedded mode), otherwise from URL params (standalone)
    const idProject = props.projectId ? { id: props.projectId } : urlParams;

    const defaultSettings = {
        showSpinner: true,
        showModule: false,
        noProjects: false 
    }

    const [loadingProgress, setLoadingProgress] = useState(null)


    const initialData = useRef(null)
    const videoPlayer = useRef(null);
    const extVPlayerRef = useRef(null);
    const originalData = useRef(false);
    const fileManagerSyncFusion = useRef(null);
    const fileManagerToolBarRef = useRef(null)
    const fileManagerModalsRef = useRef(null)

    const [fileManagerSettings, setFileManagerSettings] = useState({...defaultSettings, ...props.moduleInfo})


    useEffect(() => {

        if (!fileManagerSettings.canLoad) {
            return;
        }

        const dBCall = {
            idsProject: idProject.id, 
            assetsToLoad: props.type ?? 'full'
        }

        dBConn.post(`filemanager-projects`, dBCall).then(async (result) => {
    
            if(result.found === false){
                setFileManagerSettings(previous => ({
                    ...previous,
                    noProjects: true,
                    showModule: true
                }))

            } else {
        
            const allSourceFiles = result?.sourceFiles || [];
            const allTargetFiles = result?.targetFiles || [];

            const totalSourceFiles = result.sourceFilesIDs?.reduce((sum, block) => sum + parseInt(block.total_elements), 0) || 0;
            const totalTargetFiles = result.targetFilesIDs?.reduce((sum, block) => sum + parseInt(block.total_elements), 0) || 0;
            const totals = totalSourceFiles + totalTargetFiles;
            let currentLoaded = 0;
            setLoadingProgress(
                {text: `Loading ${currentLoaded} of ${totals}`,
                perc: 0})

            const chunkSize = 20000;
        
            // Helper to send the chunk
            const sendChunks = async (ids, isSource) => {
                for (let i = 0; i < ids.length; i += chunkSize) {
                    const chunk = ids.slice(i, i + chunkSize);
        
                    const payload = {
                        sourceIDs: isSource ? chunk : [],
                        targetIDs: isSource ? [] : chunk
                    };
       
                    const chunkResult = await dBConn.post(`filemanager-sourceFilesIDsReq`, payload);

                    currentLoaded += chunkResult.length;
                    const perc = (currentLoaded / totals) * 100;
                    setLoadingProgress(
                        {text: `Loading ${currentLoaded} of ${totals} (${perc.toFixed(1)}%)`,
                        perc: perc})
                    
                    
                    if (isSource) {
                        allSourceFiles.push(...chunkResult);
                    } else {
                        allTargetFiles.push(...chunkResult);
                    }
                }
            }
        
            // First source, then target
            if (result.sourceFilesIDs?.length > 0) {
                await sendChunks(rebuildIdsFromBlocks(result.sourceFilesIDs), true);
            }
            if (result.targetFilesIDs?.length > 0) {
                await sendChunks(rebuildIdsFromBlocks(result.targetFilesIDs), false);
            }

            const sanitizedSourceFiles = sanitizedSourceFilesPath(allSourceFiles);
            const sanitizedTargetFiles = sanitizedTargetFilesPath(allTargetFiles);


            originalData.current = {
                sourcePackages: result.sourcePackages,
                sourceFiles: sanitizedSourceFiles ?? [],
                targetPackages: result.targetPackages ?? [],
                targetFiles: sanitizedTargetFiles,
                flatView: true,
                type: result.type
            }
        
            // --- FINAL STEP ---
            initialData.current = createFolderStructure(originalData.current);
        
            setFileManagerSettings(prev => ({
                ...prev,
                breadcrumbData: result.breadcrumb,
                showModule: true
            }))
        }
        })
    }, [idProject.id, fileManagerSettings.canLoad]);

    const playVideo = (props) => {
        videoPlayer.current.playVideo(props)
    }

    const playAudio = (props) => {
        if(fileManagerToolBarRef.current){
            fileManagerToolBarRef.current.playAudio(props)
        }
    }


    // Show loading only in standalone mode until data is ready
    if(fileManagerSettings.standAlone && !initialData.current && !fileManagerSettings.noProjects){
        
        return loadingProgress ? <>
            <div>{loadingProgress.text}</div>
            <ProgressionBar value={loadingProgress.perc} />
        </> : <LoadingBar /> 
    }

    const VideoPlayerDetachState = (oldProperties) => {
        if(!extVPlayerRef.current){
            extVPlayerRef.current = window.open(window.location.origin + '/AudioMateVPlayer', '_blank', 'width=700, height=560, left=200, top=200, toolbar=no, location=no');
            //checkIntervalRefVideo.current = setInterval(checkIfWindowClosedVideo, 500)
            function handleLoad(){ 
                setTimeout(function(){ 
                    sendMessageToVideoPlayer(oldProperties.filename, oldProperties.url)
                }, 500)
                extVPlayerRef.current.addEventListener('load', handleLoad);
            }
            extVPlayerRef.current.addEventListener('load', handleLoad);
            extVPlayerRef.current.addEventListener('message', receiveMessageVideoPlayer)
        }
    }

    const sendMessageToVideoPlayer = (filename, url) => {
        const obj = {
            filename,
            url,
        }
        extVPlayerRef.current.postMessage({playInfo: obj},'*')
    }

    
    function receiveMessageVideoPlayer(props){
        if(props.data.action){
            const message = props.data.action
            if(message.type === 'destroy'){
                extVPlayerRef.current.removeEventListener('message', receiveMessageVideoPlayer)
                extVPlayerRef.current = null
                //pronCntxt.extVPlayerRefCheck.current = null;
            }    
        }
    }

    const messageFromToolbar = (data) => {

        // flat or expanded
        const {type, value} = data;

        // change between Flat or Structured subfolders
        if(type === 'flatView'){
            originalData.current.flatView = value
            initialData.current = createFolderStructure(originalData.current);        
            fileManagerSyncFusion.current.refreshFileManager(initialData.current)
            fileManagerToolBarRef.current.refreshToolBar({key: 'flatView', value: originalData.current.flatView})
        }

        // change between List or Details
        if(type === 'detailsView'){     
            fileManagerSyncFusion.current.detailsViewSwitcher(initialData.current)
        }
    }

    const onModalOperation = (props) => {
        fileManagerModalsRef.current.openModals({
            ...props, 
            dataInTable: originalData.current,
            gridData: initialData.current  // structure 
        })
    }

    const fromModals = (args) => {

        // Update the original data
        originalData.current = args.updatedData
        
        // Regenerates the structure of the grid
        initialData.current = createFolderStructure(originalData.current)
        
        // Apply operations to the filemanager (cancellations)
        fileManagerSyncFusion.current.applyOperationModal(args)
        
        // Refresh of the filemanager to show updated data
        // For the Rename, keep the current path (Keepath = True)
        // added also for deletion
        //const keepPath = args.operation === "Rename";
        fileManagerSyncFusion.current.refreshFileManager(initialData.current, true)
    }

    const handleFind = (valueInFind) => {
        const searchValue = String(valueInFind).toLowerCase();
        const results = initialData.current.filter(el => 
            el.name && el.name.toLowerCase().includes(searchValue)
        )
        
        // Replace the datasource with filtered results
        if(fileManagerSyncFusion.current){
            fileManagerSyncFusion.current.setFilteredData(results, valueInFind);
        }
        
        fileManagerToolBarRef.current.resultsFromFind(results, valueInFind)
    }
    
    const clearFind = () => {
        // Restore original data
        if(fileManagerSyncFusion.current){
            fileManagerSyncFusion.current.restoreOriginalData();
        }
    }

    const gotoPath = (path) => {
        if(fileManagerSyncFusion.current){
            fileManagerSyncFusion.current.goto(path)
        }
    }


    return (<>
        {!fileManagerSettings.noProjects ? (<>
        
        <VideoComponent ref={videoPlayer} onDetachState={VideoPlayerDetachState} />
        {fileManagerSettings.standAlone &&
            <>
            <NewProjectUpperBar breadcrumbData={fileManagerSettings.breadcrumbData} nameModule={'Assets'} />
            <ProjectSupportComponents data={10} breadcrumbData={idProject.id} />
            </>
        }
        <FileManagerModals ref={fileManagerModalsRef} fromModals={fromModals} />
        
        <div className="card" style={{height: `calc(100vh - 216px)`}}>
            <div className={fileManagerSettings.standAlone ? "card-body mt-3" : "filemanager-embedded"}>
                {initialData.current ? (
                    <>
                        <FileManagerToolBar ref={fileManagerToolBarRef} messageFromToolbar={messageFromToolbar} handleFind={handleFind} goto={gotoPath} clearFind={clearFind} />
                        <FileManagerModule 
                        ref={fileManagerSyncFusion}
                        initialData={initialData.current}
                        parsePlayAudio={playAudio}
                        playVideo={playVideo}
                        modalOperation={onModalOperation}
                        spinner={(args) => fileManagerToolBarRef.current.spinner(args)}
                         />
                    </>
                ) : (
                    <LoadingBar />
                )}
            </div>
        </div>
        </>) : 
        <div className="card">
            <div className="card-body mt-3">No Assets Found</div>
        </div>}

    </>)
}




const FileManager = (props) => {

    const [fileManagerSettings, setFileManagerSettings] = useState({
        noProjects: false,
        standAlone: Object.keys(props).length === 0 || !props.embedded,
        showModal: false,
        canLoad: Object.keys(props).length === 0 || !props.embedded, // Load only in standalone mode
        showSpinner: false, // Not used anymore
        projectId: props.projectId || null // Store projectId from props
    })

    const closeModal = () => {
        setFileManagerSettings(previous => ({
            ...previous,
            showModal: false
        }))
    }


    return fileManagerSettings.standAlone ? 
        <main className="main" id="main">
            <FileManagerApp moduleInfo={fileManagerSettings} type={props.type} />
        </main>
        : 

        <>
        <button 
            className="btn noborder asimulatedlink mt-1"
            title={props.title} 
            onClick={() => setFileManagerSettings(prev => ({...prev, showModal: true, canLoad: true}))}>
            <i className="bi bi-folder"></i>
        </button>
        {fileManagerSettings.showModal && <AMModal
            header_title = {<><i style={{position: 'relative', fontSize: '16px', top: '0px', marginRight: '8px'}} className="bi bi-folder"></i>File Manager</>}
            closeHandler = {closeModal}
            size='xl'
            contentHeight='calc(100vh - 200px)'
            >
                <FileManagerApp moduleInfo={fileManagerSettings} projectId={props.projectId} type={props.type} />
        </AMModal>}
        </>
}

export default FileManager

const rebuildIdsFromBlocks = (blocks) => {
    const allIds = [];
    
    for (const block of blocks) {
        const start = parseInt(block.start_id);
        const count = parseInt(block.total_elements);
        
        for (let i = 0; i < count; i++) {
            allIds.push(start + i);
        }
    }
    
    return allIds;
}